import { CommonModule } from '@angular/common'
import { Component, computed, inject, input } from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'

import { Store } from '@ngrx/store'

import { selectedAiUserTaskTemplateConfig } from '@flowx/designer/shared/store/selectors/template-config.selectors'

import { AiAgent } from '../../../../common/ai.dictionary'
import { IMAGE_UI_DESIGNER } from '../../../../common/icons/ai-icons.dictionary'
import { AiIconsModule } from '../../../../common/icons/ai-icons.module'
import { CtaActions } from '../../../../store/ai.actions'
import { AiSelectors } from '../../../../store/ai.selectors'
import { AiButtonComponent } from '../ai-action-button/ai-action-button.component'

type CtaPreview = {
  image: string
  title: string
  placeholder: string[]
  action: () => void
}

const aiPermissionsMap: Record<
  AiAgent.ANALYST | AiAgent.DESIGNER,
  (permissions: Record<string, boolean>) => boolean
> = {
  [AiAgent.ANALYST]: (permissions) => permissions.showAnalystAgent,
  [AiAgent.DESIGNER]: (permissions) => permissions.showDesignerAgent,
}

@Component({
  selector: 'flx-ai-cta',
  imports: [CommonModule, AiIconsModule, AiButtonComponent],
  templateUrl: './cta.component.html',
  styleUrls: ['./cta.component.scss'],
})
export class AiCtaComponent {
  #sanitizer = inject(DomSanitizer)
  #store = inject(Store)

  pendingAiResponse = this.#store.selectSignal(AiSelectors.pendingAiResponse)

  processDesignerCta: CtaPreview = {
    image: `<svg width="201" height="148" viewBox="0 0 201 148" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path class="fade" d="M24.8333 50C24.8333 51.4728 26.0272 52.6667 27.5 52.6667C28.9728 52.6667 30.1667 51.4728 30.1667 50C30.1667 48.5272 28.9728 47.3333 27.5 47.3333C26.0272 47.3333 24.8333 48.5272 24.8333 50ZM27.5 30L27.5 29.5L27 29.5L27 30L27.5 30ZM100.5 30L95.5 27.1133L95.5 32.8868L100.5 30ZM28 50L28 30L27 30L27 50L28 50ZM27.5 30.5L96 30.5L96 29.5L27.5 29.5L27.5 30.5Z" fill="#CBD1DB"/>
    <path class="fade" d="M27.5 48L27.5 30L95.5 30" stroke="#CBD1DB" stroke-width="2"/>
    <path class="fade" d="M24.8333 98C24.8333 96.5272 26.0272 95.3333 27.5 95.3333C28.9728 95.3333 30.1667 96.5272 30.1667 98C30.1667 99.4728 28.9728 100.667 27.5 100.667C26.0272 100.667 24.8333 99.4728 24.8333 98ZM27.5 118L27.5 118.5L27 118.5L27 118L27.5 118ZM100.5 118L95.5 120.887L95.5 115.113L100.5 118ZM28 98L28 118L27 118L27 98L28 98ZM27.5 117.5L96 117.5L96 118.5L27.5 118.5L27.5 117.5Z" fill="#CBD1DB"/>
    <path class="fade" d="M27.5 100L27.5 118L95.5 118" stroke="#CBD1DB" stroke-width="2"/>

    <rect x="27.5" y="48.4142" width="36.1838" height="36.1838" transform="rotate(45 27.5 48.4142)" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2"/>
    <rect x="102.5" y="90" width="97" height="57" rx="7" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2"/>
    <rect x="102.5" y="1" width="97" height="57" rx="7" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2"/>
    <rect x="121.5" y="17" width="60" height="25" rx="4" fill="#E3E8ED"/>
    <rect x="121.5" y="106" width="60" height="25" rx="4" fill="#E3E8ED"/>
  </svg>`,
    title: 'Process Designer',
    placeholder: ['Drag BPMN nodes from the sidebar', ' or have the AI', 'the process for you'],
    action: () => this.#store.dispatch(CtaActions.processDesignerCtaClick()),
  }

  processLoadingCta: CtaPreview = {
    image: `<svg width="504" height="154" viewBox="0 0 504 154" rol="img" aria-labelledby="title desc">
    <g transform="scale(1.2)" transform-origin="0 0">
      <title id="title">Animated BPMN-style loader</title>
      <desc id="desc">A start event, a task, a gateway, and an end event with a token moving along the sequence flow.</desc>

      <defs>
        <!-- Arrowhead for sequence flow -->
        <marker id="arrow" orient="auto" markerWidth="5" markerHeight="5" refX="5" refY="2.5">
          <path d="M0,0 L5,2.5 L0,5 Z" fill="#4c6fff" stroke="#4c6fff" stroke-width="1"></path>
        </marker>
        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0f172a" flood-opacity="0.15" />
        </filter>
      </defs>

      <!-- Sequence flow path (token follows this) -->
      <path id="flow" class="flow" d="M56,85 H100 H200 L220,85 L280,85 L342,85" marker-end="url(#arrow)"/>

      <!-- Start Event -->
      <g transform="translate(40,85)">
        <circle r="16" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2" filter="url(#softShadow)" />
      </g>

      <!-- Task (rounded rectangle) -->
      <g transform="translate(100,55)">
        <rect x="0" y="0" rx="10" ry="10" width="100" height="60" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2" filter="url(#softShadow)" />
        <text x="50" y="36" text-anchor="middle" font-size="12" fill="#000000aa" opacity=".9">Task</text>
      </g>

      <!-- Gateway (diamond) -->
      <g transform="translate(250,85)">
        <polygon points="0,-25 25,0 0,25 -25,0" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2" filter="url(#softShadow)" />
        <path d="M-10,0 h20 M0,-10 v20" stroke="#CBD1DB" stroke-width="2"/>
      </g>

      <!-- End Event (thicker) -->
      <g transform="translate(360,85)">
        <circle r="18" fill="#F7F8F9" stroke="#CBD1DB" stroke-width="2" filter="url(#softShadow)" />
        <circle r="14" fill="none" stroke="#CBD1DB" stroke-width="3.5" />
      </g>

      <!-- Moving token -->
      <circle class="token" r="4" fill="#feb913" filter="url(#softShadow)">
        <animateMotion dur="3s" repeatCount="indefinite" rotate="auto">
          <mpath href="#flow" />
        </animateMotion>
      </circle>
    </g>
  </svg>`,
    title: 'Process Designer',
    placeholder: ['Drag BPMN nodes from the sidebar', ' or have the AI', 'the process for you'],
    action: () => this.#store.dispatch(CtaActions.processDesignerCtaClick()),
  }

  uiDesignerCta: CtaPreview = {
    image: IMAGE_UI_DESIGNER,
    title: 'UI Designer',
    placeholder: ['Drag components from the sidebar', ' or have the AI', 'the layout for you'],
    action: (): void => {
      const rootUserTaskTemplateConfig = this.#store.selectSignal(
        selectedAiUserTaskTemplateConfig
      )()
      if (rootUserTaskTemplateConfig) {
        this.#store.dispatch(
          CtaActions.uiDesignerCtaClick({
            userTaskNavigationAreaId: rootUserTaskTemplateConfig.navigationAreaId,
          })
        )
      }
    },
  }

  uiFlowDesignerCta: CtaPreview = {
    image: IMAGE_UI_DESIGNER,
    title: 'UI Flows Designer',
    placeholder: ['Drag components from the sidebar', ' or have the AI', 'the layout for you'],
    action: (): void => {
      this.#store.dispatch(CtaActions.uiFlowDesignerCtaClick())
    },
  }

  ctaPreviewMap: Record<AiAgent.ANALYST | AiAgent.DESIGNER, CtaPreview> = {
    [AiAgent.ANALYST]: this.processLoadingCta,
    [AiAgent.DESIGNER]: this.uiDesignerCta,
  }

  agent = input<AiAgent>()
  context = input<'process' | 'uiFlow'>('process')

  preview = computed<CtaPreview | null>(() => {
    if (this.agent() === AiAgent.DESIGNER && this.context() === 'uiFlow') {
      return this.uiFlowDesignerCta
    }
    return this.ctaPreviewMap[this.agent()] || null
  })
  svg = computed(() => {
    const preview = this.preview()
    return preview?.image ? this.#sanitizer.bypassSecurityTrustHtml(preview.image) : null
  })
  hasAiPermissions = computed(() => {
    const agent = this.agent()
    const permissions = this.#store.selectSignal(AiSelectors.permissions)()

    return aiPermissionsMap[agent]?.(permissions)
  })
}
